import {useEffect, useMemo, useState} from 'react';
import {Form, Select, Switch} from 'antd';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import styles from './mobileSetting.module.scss';
import {isDabaEmail} from 'utils/helper';

const NotificationWorkflowGovernance = ({data, refetch}) => {
  const [enabled, setEnabled] = useState(false);
  const [governanceEnabled, setGovernanceEnabled] = useState(false);
  const [defaultApprovers, setDefaultApprovers] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.PlatformSettings),
  );

  const [updateAppConfig, {loading: updateLoading}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (data) {
      setEnabled(!!data.enabled);
      setGovernanceEnabled(!!data.governanceEnabled);
      setDefaultApprovers(data.defaultApprovers || []);
    }
  }, [data]);

  const invalidEmails = useMemo(
    () => defaultApprovers.filter(email => !isDabaEmail(email)),
    [defaultApprovers],
  );

  const openConfirm = () => {
    if (invalidEmails.length) {
      toast.error(
        `Use official daba emails only — invalid: ${invalidEmails.join(', ')}`,
      );
      return;
    }
    setConfirmOpen(true);
  };

  const submit = () => {
    updateAppConfig({
      variables: {
        data: {
          notificationOrchestrator: {
            enabled,
            governanceEnabled,
            defaultApprovers,
          },
        },
      },
    }).then(({data: result}) => {
      if (!result) return;
      if (result.updateAppConfig?.__typename === 'Error') {
        toast.error(result.updateAppConfig.message);
      } else {
        toast.success('Governance settings updated');
        setConfirmOpen(false);
        refetch?.();
      }
    });
  };

  return (
    <div className={styles.body}>
      <div className={styles.top}>
        <h1>Notification Workflow Governance</h1>
        <p>
          Master controls for the notification orchestrator. Cooldown and
          approval rules apply only when the gatekeeper is enabled.
        </p>
      </div>

      <div className={styles.governanceRow}>
        <div>
          <div className={styles.governanceLabel}>Enable gatekeeper</div>
          <div className={styles.governanceHelper}>
            Master switch. When off, notifications dispatch immediately and
            bucket rate-limit and cooldown rules are skipped.
          </div>
        </div>
        <Switch
          checked={enabled}
          onChange={setEnabled}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      </div>

      <div className={styles.governanceRow}>
        <div>
          <div className={styles.governanceLabel}>Require approval flow</div>
          <div className={styles.governanceHelper}>
            When on, notifications in buckets that require approval start as
            DRAFT and approvers are emailed for sign-off.
          </div>
        </div>
        <Switch
          checked={governanceEnabled}
          onChange={setGovernanceEnabled}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      </div>

      <div className={styles.governanceApprovers}>
        <span className={styles.governanceLabel}>Fallback approver emails</span>
        <div className={styles.governanceHelper}>
          Used when a bucket needs approval but its verifiers and per-bucket
          defaultApprovers all resolve to nobody. Must be official daba emails.
        </div>
        <Form layout="vertical">
          <Form.Item
            validateStatus={invalidEmails.length ? 'error' : ''}
            help={
              invalidEmails.length
                ? `Use official daba emails only — invalid: ${invalidEmails.join(
                    ', ',
                  )}`
                : null
            }>
            <Select
              mode="tags"
              placeholder="ops@dabafinance.com"
              value={defaultApprovers}
              onChange={setDefaultApprovers}
              tokenSeparators={[',', ' ']}
              style={{width: '100%'}}
            />
          </Form.Item>
        </Form>
      </div>

      <SubmitButton
        label="Update Settings"
        disabled={
          updateLoading || !tabOperations?.update || invalidEmails.length > 0
        }
        handleClick={openConfirm}
      />

      <ConfirmModal
        title="Update Governance Settings"
        openModal={confirmOpen}
        setOpenModal={setConfirmOpen}
        loading={updateLoading}
        handleOperation={submit}
        confirmText={
          <p className={styles.governanceConfirmText}>
            This will change platform-wide notification governance.{' '}
            {enabled ? (
              <>
                The gatekeeper will be <strong>enabled</strong> — bucket
                cooldowns and rate-limits apply.
              </>
            ) : (
              <>
                The gatekeeper will be <strong>disabled</strong> — notifications
                bypass the gate.
              </>
            )}{' '}
            {governanceEnabled
              ? 'Approval flow stays on for buckets that require it.'
              : 'Approval flow stays off.'}
          </p>
        }
      />
    </div>
  );
};

export default NotificationWorkflowGovernance;
